import { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import {
  Box,
  Typography,
  Button,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  CircularProgress,
  Alert,
  Chip,
  Snackbar,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Tabs,
  Tab,
} from '@mui/material';
import { Add, Edit, Delete, Extension, Search } from '@mui/icons-material';
import { DataGrid, GridColDef, GridRenderCellParams } from '@mui/x-data-grid';
import { ModuleLayout } from '../../components/Layout/ModuleLayout';
import PendingApprovalsSection from '../../components/common/PendingApprovalsSection';
import { moduleService, modulePartService, moduleScheduleService, ModulePart } from '../../services';
import { usePermission } from '../../hooks';
import { colors } from '../../theme/theme';

export const ModulePartsPage = () => {
  const { id } = useParams<{ id: string }>();
  const { can } = usePermission();
  const [module, setModule] = useState<any>(null);
  const [parts, setParts] = useState<any[]>([]);
  const [schedules, setSchedules] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [openDialog, setOpenDialog] = useState(false);
  const [selectedPart, setSelectedPart] = useState<any>(null);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    schedule_id: '',
  });
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success' as 'success' | 'error',
  });
  const [searchQuery, setSearchQuery] = useState('');
  const [tabValue, setTabValue] = useState(0);
  const [paginationModel, setPaginationModel] = useState({
    page: 0,
    pageSize: 10,
  });

  useEffect(() => {
    fetchData();
  }, [id]);

  // Filter parts based on search query
  const filteredParts = parts.filter((part) => {
    if (!searchQuery) return true;
    const query = searchQuery.toLowerCase();
    return (
      part.name?.toLowerCase().includes(query) ||
      part.description?.toLowerCase().includes(query) ||
      part.schedule?.name?.toLowerCase().includes(query)
    );
  });

  const fetchData = async () => {
    try {
      setLoading(true);
      const [moduleRes, partsRes, schedulesRes] = await Promise.all([
        moduleService.getModule(Number(id)),
        modulePartService.getAllForCounty(), // Fetch all county parts
        moduleScheduleService.getAllForCounty(), // Fetch all county schedules
      ]);

      if (moduleRes.status === 'success') {
        setModule(moduleRes.data);
      }
      if (partsRes.status === 'success' && partsRes.data) {
        const partsData = partsRes.data || [];
        setParts(Array.isArray(partsData) ? partsData : []);
      }
      if (schedulesRes.status === 'success' && schedulesRes.data) {
        const schedulesData = schedulesRes.data || [];
        setSchedules(Array.isArray(schedulesData) ? schedulesData : []);
      }
    } catch (err: any) {
      setError(err.response?.data?.message || err.message || 'Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const handleOpenDialog = (part?: any) => {
    if (part) {
      setSelectedPart(part);
      setFormData({
        name: part.name || '',
        description: part.description || '',
        schedule_id: part.schedule_id?.toString() || '',
      });
    } else {
      setSelectedPart(null);
      setFormData({ name: '', description: '', schedule_id: '' });
    }
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setSelectedPart(null);
    setFormData({ name: '', description: '', schedule_id: '' });
  };

  const handleSubmit = async () => {
    try {
      const data = {
        ...formData,
        schedule_id: formData.schedule_id ? parseInt(formData.schedule_id) : undefined,
      };

      let response;
      if (selectedPart) {
        response = await modulePartService.update(selectedPart.id, data);
        if (response.status === 'success') {
          setSnackbar({
            open: true,
            message: response.message || 'Update request submitted for approval',
            severity: 'success',
          });
          setTabValue(1);
        }
      } else {
        response = await modulePartService.create(data);
        if (response.status === 'success') {
          setSnackbar({
            open: true,
            message: 'Part created successfully',
            severity: 'success',
          });
        }
      }
      fetchData();
      handleCloseDialog();
    } catch (err: any) {
      console.error('Failed to save part:', err);
      setSnackbar({
        open: true,
        message: err.response?.data?.message || 'Failed to save part',
        severity: 'error',
      });
    }
  };

  const handleDelete = async (partId: number) => {
    if (!window.confirm('Are you sure you want to submit a delete request for this part?')) return;
    try {
      const response = await modulePartService.delete(partId);
      if (response.status === 'success') {
        setSnackbar({
          open: true,
          message: response.message || 'Delete request submitted for approval',
          severity: 'success',
        });
        setTabValue(1);
        fetchData();
      }
    } catch (err: any) {
      console.error('Failed to delete part:', err);
      setSnackbar({
        open: true,
        message: err.response?.data?.message || 'Failed to delete part',
        severity: 'error',
      });
    }
  };

  const columns: GridColDef[] = [
    {
      field: 'name',
      headerName: 'Name',
      width: 250,
      display: 'flex',
      renderCell: (params: GridRenderCellParams) => (
        <Typography variant="body2" sx={{ fontWeight: 600 }}>
          {params.value}
        </Typography>
      ),
    },
    {
      field: 'schedule',
      headerName: 'Schedule',
      width: 200,
      display: 'flex',
      renderCell: (params: GridRenderCellParams) => (
        <Chip
          label={params.value?.name || 'N/A'}
          size="small"
          sx={{ fontWeight: 600, bgcolor: '#eff6ff', color: '#1e40af' }}
        />
      ),
    },
    {
      field: 'description',
      headerName: 'Description',
      width: 300,
      display: 'flex',
      renderCell: (params: GridRenderCellParams) => (
        <Typography variant="body2" color="text.secondary">
          {params.value || 'N/A'}
        </Typography>
      ),
    },
    {
      field: 'status',
      headerName: 'Status',
      width: 120,
      display: 'flex',
      renderCell: (params: GridRenderCellParams) => (
        <Chip
          label={params.value || 'Active'}
          color="success"
          size="small"
          sx={{ fontWeight: 600 }}
        />
      ),
    },
    {
      field: 'actions',
      headerName: 'Actions',
      width: 120,
      sortable: false,
      display: 'flex',
      renderCell: (params: GridRenderCellParams) => (
        <Box sx={{ display: 'flex', gap: 0.5 }}>
          {can('parts.manage') && (
            <IconButton
              size="small"
              onClick={() => handleOpenDialog(params.row)}
              sx={{ color: colors.primary.main }}
            >
              <Edit fontSize="small" />
            </IconButton>
          )}
          {can('parts.manage') && (
            <IconButton
              size="small"
              onClick={() => handleDelete(params.row.id)}
              sx={{ color: '#dc2626' }}
            >
              <Delete fontSize="small" />
            </IconButton>
          )}
        </Box>
      ),
    },
  ];

  if (loading) {
    return (
      <ModuleLayout module={module} moduleId={id!}>
        <Box display="flex" justifyContent="center" py={8}>
          <CircularProgress />
        </Box>
      </ModuleLayout>
    );
  }

  return (
    <ModuleLayout module={module} moduleId={id!}>
      <Box sx={{ p: 3 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 4 }}>
          <Box>
            <Typography variant="h4" sx={{ fontWeight: 700, color: '#1f2937', mb: 1 }}>
              Parts
            </Typography>
            <Typography variant="body2" sx={{ color: 'text.secondary' }}>
              Manage parts (available across all modules)
            </Typography>
          </Box>
          {can('parts.manage') && (
            <Button
              variant="contained"
              startIcon={<Add />}
              onClick={() => handleOpenDialog()}
              sx={{
                background: colors.gradients.primary,
                boxShadow: colors.shadows.button,
                '&:hover': {
                  background: colors.gradients.primaryHover,
                  boxShadow: colors.shadows.buttonHover,
                  transform: 'translateY(-1px)',
                },
              }}
            >
              Add Part
            </Button>
          )}
        </Box>

        <Tabs
          value={tabValue}
          onChange={(_, newValue) => setTabValue(newValue)}
          sx={{ mb: 3, borderBottom: 1, borderColor: 'divider' }}
        >
          <Tab label="Parts" />
          <Tab label="Approvals" />
        </Tabs>

        {tabValue === 0 && (
          <>
            <TextField
              fullWidth
              placeholder="Search parts by name, description, or schedule..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              sx={{ mb: 3 }}
              InputProps={{
                startAdornment: <Search sx={{ color: 'text.secondary', mr: 1 }} />,
              }}
            />

            {error && (
              <Alert severity="error" sx={{ mb: 3 }}>
                {error}
              </Alert>
            )}

            <DataGrid
              rows={filteredParts}
              columns={columns}
              paginationModel={paginationModel}
              onPaginationModelChange={setPaginationModel}
              pageSizeOptions={[5, 10, 25, 50, 100]}
              disableRowSelectionOnClick
              sx={{
                height: 600,
                border: 'none',
                bgcolor: 'white',
                borderRadius: 4,
                '& .MuiDataGrid-cell:focus': {
                  outline: 'none',
                },
                '& .MuiDataGrid-row:hover': {
                  bgcolor: '#f9fafb',
                },
              }}
              slots={{
                noRowsOverlay: () => (
                  <Box sx={{ textAlign: 'center', py: 8 }}>
                    <Box
                      sx={{
                        width: 80,
                        height: 80,
                        borderRadius: '50%',
                        bgcolor: colors.primary[50],
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        mx: 'auto',
                        mb: 3,
                      }}
                    >
                      <Extension sx={{ fontSize: 40, color: colors.primary.main }} />
                    </Box>
                    <Typography variant="h6" sx={{ fontWeight: 700, mb: 1 }}>
                      {searchQuery ? 'No Matching Parts' : 'No Parts Found'}
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                      {searchQuery
                        ? 'Try adjusting your search query'
                        : 'Create your first part (will be available across all modules)'
                      }
                    </Typography>
                    {!searchQuery && can('parts.manage') && (
                      <Button
                        variant="contained"
                        startIcon={<Add />}
                        onClick={() => handleOpenDialog()}
                        sx={{
                          background: colors.gradients.primary,
                        }}
                      >
                        Add Part
                      </Button>
                    )}
                  </Box>
                ),
              }}
            />
          </>
        )}

        {tabValue === 1 && (
          <PendingApprovalsSection
            entityType="part"
            title="Pending Part Approvals"
            onApprovalComplete={fetchData}
          />
        )}

        <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
          <DialogTitle>{selectedPart ? 'Edit Part' : 'Add Part'}</DialogTitle>
          <DialogContent>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 2 }}>
              <TextField
                fullWidth
                required
                label="Part Name"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              />
              <TextField
                fullWidth
                label="Description"
                multiline
                rows={3}
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              />
              <FormControl fullWidth required>
                <InputLabel>Schedule</InputLabel>
                <Select
                  value={formData.schedule_id}
                  label="Schedule"
                  onChange={(e) => setFormData({ ...formData, schedule_id: e.target.value })}
                >
                  <MenuItem value="">
                    <em>Select a schedule</em>
                  </MenuItem>
                  {schedules.map((schedule) => (
                    <MenuItem key={schedule.id} value={schedule.id}>
                      {schedule.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>
          </DialogContent>
          <DialogActions sx={{ p: 3 }}>
            <Button onClick={handleCloseDialog}>Cancel</Button>
            <Button
              variant="contained"
              onClick={handleSubmit}
              sx={{
                background: colors.gradients.primary,
              }}
            >
              {selectedPart ? 'Update' : 'Create'}
            </Button>
          </DialogActions>
        </Dialog>

        <Snackbar
          open={snackbar.open}
          autoHideDuration={4000}
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        >
          <Alert
            onClose={() => setSnackbar({ ...snackbar, open: false })}
            severity={snackbar.severity}
            sx={{ width: '100%' }}
          >
            {snackbar.message}
          </Alert>
        </Snackbar>
      </Box>
    </ModuleLayout>
  );
};
